/**
 * Shared schemas and helpers for rest-api and its test mock.
 * Single source of truth to avoid duplication between rest-api.ts and __mocks__/rest-api.ts.
 */

import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { z, ZodError } from 'zod';

import { stripNull, stripUndefined } from '~/utils/helpers';

export const defaultMutationResponseSchema = z.object({ message: z.string() });
export type DefaultMutationResponse = z.infer<
  typeof defaultMutationResponseSchema
>;

export const isMutationSuccess = <T = unknown>(
  result:
    | { data: T }
    | { error: FetchBaseQueryError | SerializedError },
): result is { data: T } => {
  if ('data' in result) return true;
  return false;
};

export const paginationMetaSchema = z.object({
  count: z.coerce.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  hasNext: z.boolean().optional().default(false),
  hasPrevious: z.boolean().optional().default(false),
});

export const parsePaginatedResponse = <Schema extends z.ZodTypeAny>(
  schema: Schema,
  data: unknown,
) => {
  return paginationMetaSchema
    .transform((values) => ({
      ...values,
      hasNext: values.next !== null,
      hasPrevious: values.previous !== null,
    }))
    .and(z.object({ results: schema.array() }))
    .parse(data);
};

export const paginatedParamsSchema = z.object({ page: z.coerce.number() });
export type PaginatedResponse<Data> = z.infer<typeof paginationMetaSchema> & {
  results: Data[];
};

export const toParams = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rawParams: Record<string, any>,
  options?: {
    stripNull?: boolean;
  },
) => {
  const { stripNull: shouldStripNull = true } = options ?? {};
  const paramArg = rawParams;
  let parsed = stripUndefined(paramArg ?? {});
  if (shouldStripNull) {
    parsed = stripNull(parsed);
  }

  return new URLSearchParams(parsed);
};

export function parseOrReturnError<T>(fn: () => T) {
  try {
    return { data: fn() };
  } catch (caughtError) {
    let error: FetchBaseQueryError;
    if (caughtError instanceof ZodError) {
      error = {
        status: 'CUSTOM_ERROR',
        error: 'Failed to parse the response data',
      };
    } else {
      error = {
        status: 'CUSTOM_ERROR',
        error: 'Failed to parse data',
      };
    }
    return { error };
  }
}

/**
 * @ignore
 */
export function useWrappedMutation<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Trigger extends (...args: any[]) => any,
  Result,
  TMutation extends Readonly<[Trigger, Result]>,
  TriggerParameters extends Parameters<TMutation[0]>,
  MutationResponse extends Exclude<
    Awaited<ReturnType<TMutation[0]>>,
    { error: unknown }
  >,
>(args: TMutation) {
  return {
    mutateAsync: async (
      parameters: TriggerParameters[0],
      callbacks?: {
        onSuccess?: (data: MutationResponse['data']) => void;
        onError?: (error: FetchBaseQueryError | SerializedError) => void;
      },
    ) => {
      const result = await args[0](parameters);
      if (isMutationSuccess(result)) {
        callbacks?.onSuccess?.(result.data as MutationResponse['data']);
      } else {
        callbacks?.onError?.(
          result.error as FetchBaseQueryError | SerializedError,
        );
      }
      return result as Awaited<ReturnType<TMutation[0]>>;
    },
    state: args[1] as TMutation[1],
  };
}
